/**
 * src/state/vulnerabilities/projectOverride.js
 *
 * Client-side optimistic projection of a new vulnerabilityOverride onto
 * the matching state.vulnerabilities[] row.
 *
 * Why this exists:
 *   The canonical correlator runs server-side (hourly :15) and is the
 *   sole authority on state.vulnerabilities[]. But the dashboard UX needs
 *   instant feedback when an admin clicks "Suppress" — waiting up to an
 *   hour for the row to flip is unacceptable.
 *
 *   This helper performs the same projection the server-side correlator
 *   would perform on the next tick, so the dashboard reflects the change
 *   immediately. The next correlator run produces an equivalent result
 *   deterministically (boundary VUL-B-002).
 *
 * Boundary VUL-B-003: triage decisions live in vulnerabilityOverrides[]
 * (which we always write); the row mutation here is a *projection* of
 * that override, not a replacement for it.
 *
 * NOTE: this file mirrors the override-projection logic in
 * azure-functions/shared/vulnerabilities/correlator.js
 * (`applyOverridesForFinding`). The CJS / ESM split prevents direct
 * import. Keep both in sync; a parity check belongs in Phase 7.
 */

/**
 * Produces a new override + the patched vulnerability row.
 * Does NOT mutate inputs.
 *
 * @param {object} vuln           the vulnerability row being overridden
 * @param {string} decision       'suppress' | 'accept' | 'defer' | 'reopen'
 *                                | 'set-actor' | 'assign' | 'set-due-date'
 * @param {object} options
 * @param {string} options.justification     required, surfaced in audit log
 * @param {object} [options.payload]         decision-specific data
 * @param {string} options.createdBy         userId or displayName
 * @param {string} options.nowIso            ISO timestamp from caller
 * @returns {{ override: object, projectedVuln: object }}
 */
export function buildOverrideAndProjection(vuln, decision, options) {
  if (!vuln || !vuln.vulnerabilityKey) {
    throw new Error('buildOverrideAndProjection: vuln with vulnerabilityKey required');
  }
  if (!decision) {
    throw new Error('buildOverrideAndProjection: decision required');
  }
  if (!options?.justification?.trim()) {
    throw new Error('buildOverrideAndProjection: justification required');
  }

  const override = {
    id: `ovr-${cryptoRandomId()}`,
    vulnerabilityKey: vuln.vulnerabilityKey,
    cveId: vuln.cveId,
    assetId: vuln.assetId ?? null,
    softwareId: vuln.softwareId ?? null,
    scope: 'finding',
    decision,
    justification: options.justification.trim(),
    payload: options.payload || undefined,
    version: 1,
    createdAt: options.nowIso,
    createdBy: options.createdBy || 'unknown',
  };

  const projectedVuln = applyOverrideToRow(vuln, override);
  return { override, projectedVuln };
}

/**
 * Pure projection: returns a new vulnerability row with effective fields
 * updated to reflect the override. Mirrors the server-side correlator's
 * applyOverridesForFinding for the single-override case (which is all
 * we need for an immediate-after-write projection).
 */
export function applyOverrideToRow(vuln, override) {
  const next = { ...vuln };
  next.appliedOverrideIds = [...(vuln.appliedOverrideIds || []), override.id];

  switch (override.decision) {
    case 'suppress':
      next.effectiveStatus = 'suppressed';
      break;
    case 'accept':
      next.effectiveStatus = 'accepted';
      break;
    case 'defer':
      next.effectiveStatus = 'deferred';
      break;
    case 'reopen':
      next.effectiveStatus = 'open';
      break;
    case 'set-actor':
      if (override.payload?.actor === 'internal' || override.payload?.actor === 'external') {
        next.effectiveActor = override.payload.actor;
      }
      break;
    case 'assign':
      if (override.payload?.assigneeId) next.effectiveAssignee = override.payload.assigneeId;
      break;
    case 'set-due-date':
      if (override.payload?.dueDate) next.effectiveDueDate = override.payload.dueDate;
      break;
    default:
      break;
  }
  return next;
}

/**
 * Helper for setSoftwareOwnership: build an ownedSoftwareRegistry entry
 * and the new row that should replace any existing entry for the same
 * (vendor, product) pair.
 */
export function buildOwnedSoftwareEntry(vendor, product, classification, options) {
  if (!vendor || !product) throw new Error('buildOwnedSoftwareEntry: vendor + product required');
  if (classification !== 'internal' && classification !== 'external') {
    throw new Error('buildOwnedSoftwareEntry: classification must be internal | external');
  }
  return {
    id: `own-${cryptoRandomId()}`,
    vendor,
    product,
    classification,
    notes: options?.notes || null,
    createdAt: options?.nowIso || new Date().toISOString(),
    createdBy: options?.createdBy || 'unknown',
    updatedAt: options?.nowIso || new Date().toISOString(),
    updatedBy: options?.createdBy || 'unknown',
  };
}

function cryptoRandomId() {
  // crypto.randomUUID is available in modern browsers; fall back when not.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
